import { useState, useEffect, useRef } from 'react';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import './Sections.css';

gsap.registerPlugin(ScrollTrigger);

const statesList = [
  'Johor','Kedah','Kelantan','Melaka','Negeri Sembilan','Pahang','Perak',
  'Perlis','Pulau Pinang','Sabah','Sarawak','Selangor','Terengganu',
  'WP Kuala Lumpur','WP Putrajaya','WP Labuan',
];

const API_URL = process.env.REACT_APP_API_URL || '';

export default function OrderSection() {
  const ref = useRef();
  const [form, setForm] = useState({
    firstName:'', lastName:'', email:'', phone:'',
    address:'', city:'', postcode:'', state:'', notes:'',
  });
  const [errors, setErrors] = useState({});
  const [status, setStatus] = useState('idle'); // idle | loading | success | error
  const [errorMsg, setErrorMsg] = useState('');

  const required = ['firstName','lastName','email','phone','address','city','postcode','state'];

  useEffect(() => {
    const ctx = gsap.context(() => {
      gsap.fromTo('.order-wrap', { opacity: 0, y: 40 },
        { opacity: 1, y: 0, duration: 1, ease: 'power4.out', scrollTrigger: { trigger: ref.current, start: 'top 65%' } });
    }, ref);
    return () => ctx.revert();
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm(p => ({ ...p, [name]: value }));
    if (errors[name]) setErrors(p => ({ ...p, [name]: false }));
    if (status === 'error') setStatus('idle');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validate
    const errs = {};
    required.forEach(f => { if (!form[f].trim()) errs[f] = true; });

    // Basic email check
    if (form.email && !/\S+@\S+\.\S+/.test(form.email)) errs.email = true;

    // Basic phone check
    if (form.phone && form.phone.replace(/[\s-]/g, '').length < 9) errs.phone = true;

    setErrors(errs);
    if (Object.keys(errs).length) return;

    setStatus('loading');
    setErrorMsg('');

    try {
      const res = await fetch(`${API_URL}/api/create-bill`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(form),
      });

      const data = await res.json();

      if (res.ok && data.success) {
        setStatus('success');
        // Redirect to Billplz payment page
        setTimeout(() => {
          window.location.href = data.payment_url;
        }, 1000);
      } else {
        setStatus('error');
        setErrorMsg(data.error || 'Gagal membuat pesanan. Sila cuba lagi.');
      }
    } catch (err) {
      setStatus('error');
      setErrorMsg('Ralat sambungan. Pastikan anda mempunyai internet dan cuba lagi.');
    }
  };

  const btnText = {
    idle: 'Bayar Sekarang \u2014 RM250',
    loading: 'Memproses...',
    success: 'Mengalihkan ke pembayaran...',
    error: 'Cuba Lagi \u2014 RM250',
  };

  const btnClass = {
    idle: 'btn-gold',
    loading: 'btn-gold btn-loading',
    success: 'btn-gold btn-ok',
    error: 'btn-gold',
  };

  return (
    <section className="sec order-sec" id="order-form" ref={ref}>
      <div className="container">
        <div className="order-grid">
          <div className="order-wrap">
            <h2>Maklumat Pesanan</h2>
            <p className="order-desc">Isi maklumat di bawah untuk membuat pesanan anda.</p>
            <form onSubmit={handleSubmit}>
              <div className="frow">
                <div className="fg">
                  <label>Nama Pertama <span className="req">*</span></label>
                  <input name="firstName" value={form.firstName} onChange={handleChange} placeholder="Ahmad" className={errors.firstName?'err':''} />
                </div>
                <div className="fg">
                  <label>Nama Akhir <span className="req">*</span></label>
                  <input name="lastName" value={form.lastName} onChange={handleChange} placeholder="bin Abdullah" className={errors.lastName?'err':''} />
                </div>
              </div>
              <div className="fg">
                <label>Alamat Emel <span className="req">*</span></label>
                <input type="email" name="email" value={form.email} onChange={handleChange} placeholder="anda@email.com" className={errors.email?'err':''} />
              </div>
              <div className="fg">
                <label>Nombor Telefon <span className="req">*</span></label>
                <input type="tel" name="phone" value={form.phone} onChange={handleChange} placeholder="012-345 6789" className={errors.phone?'err':''} />
              </div>
              <div className="fg">
                <label>Alamat Penghantaran <span className="req">*</span></label>
                <input name="address" value={form.address} onChange={handleChange} placeholder="No. 123, Jalan Contoh" className={errors.address?'err':''} />
              </div>
              <div className="frow">
                <div className="fg">
                  <label>Bandar <span className="req">*</span></label>
                  <input name="city" value={form.city} onChange={handleChange} placeholder="Kuala Lumpur" className={errors.city?'err':''} />
                </div>
                <div className="fg">
                  <label>Poskod <span className="req">*</span></label>
                  <input name="postcode" value={form.postcode} onChange={handleChange} placeholder="50000" className={errors.postcode?'err':''} />
                </div>
              </div>
              <div className="fg">
                <label>Negeri <span className="req">*</span></label>
                <select name="state" value={form.state} onChange={handleChange} className={errors.state?'err':''}>
                  <option value="">Pilih Negeri</option>
                  {statesList.map(s => <option key={s} value={s}>{s}</option>)}
                </select>
              </div>
              <div className="fg">
                <label>Nota Pesanan (Optional)</label>
                <textarea name="notes" value={form.notes} onChange={handleChange} rows="3" placeholder="Sebarang arahan khas untuk penghantaran..." />
              </div>

              <div className="order-mob-summary">
                <div className="oms-row"><span>Buku TLP + 3 Bonus</span><strong>RM250</strong></div>
                <div className="oms-row"><span>Penghantaran</span><span className="free-tag">PERCUMA</span></div>
                <div className="oms-total"><span>Jumlah</span><span>RM250</span></div>
              </div>

              {errorMsg && <div className="order-error">{errorMsg}</div>}

              <button
                type="submit"
                className={`${btnClass[status]} order-submit`}
                disabled={status === 'loading' || status === 'success'}
              >
                {status === 'loading' && <span className="spinner" />}
                {btnText[status]}
              </button>

              <p className="secure-note">
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <rect x="3" y="11" width="18" height="11" rx="2" ry="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/>
                </svg>
                Pembayaran selamat melalui Billplz FPX / Online Banking
              </p>
            </form>
          </div>

          <div className="order-side">
            <div className="side-card">
              <h4>Ringkasan Pesanan</h4>
              <div className="side-product">
                <img src="/book-cover.png" alt="Buku TLP" className="side-book-img" />
                <div><strong>Buku TLP - Pakej Lengkap</strong><span>Hard Copy + 3 Bonus</span></div>
              </div>
              <div className="side-rows">
                <div className="sr"><span>Subtotal</span><span>RM250</span></div>
                <div className="sr"><span>Penghantaran</span><span className="free-tag">PERCUMA</span></div>
              </div>
              <div className="side-total"><span>Jumlah</span><span>RM250</span></div>
            </div>

            <div className="side-card payment-partner-card">
              <h4>Kaedah Pembayaran</h4>
              <div className="billplz-badge">
                <span className="powered-by">Powered by</span>
                <img src="/billplz-logo.png" alt="Billplz" className="billplz-logo" />
              </div>
              <div className="trust-r">
                <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="1" y="4" width="22" height="16" rx="2" ry="2"/><line x1="1" y1="10" x2="23" y2="10"/></svg>
                FPX Online Banking (Semua Bank)
              </div>
              <div className="payment-logos">
                <span className="bank-tag">Maybank</span>
                <span className="bank-tag">CIMB</span>
                <span className="bank-tag">RHB</span>
                <span className="bank-tag">Public Bank</span>
                <span className="bank-tag">+16 lagi</span>
              </div>
              <p className="payment-note">Anda akan dialihkan ke Billplz untuk melengkapkan pembayaran melalui FPX.</p>
            </div>

            <div className="side-card">
              <h4>Mengapa Beli Dari Kami?</h4>
              <div className="trust-r"><svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/></svg>100% Money-Back Guarantee</div>
              <div className="trust-r"><svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="1" y="3" width="15" height="13"/><polygon points="16 8 20 8 23 11 23 16 16 16 16 8"/><circle cx="5.5" cy="18.5" r="2.5"/><circle cx="18.5" cy="18.5" r="2.5"/></svg>Free Shipping Seluruh Malaysia</div>
              <div className="trust-r"><svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="3" y="11" width="18" height="11" rx="2" ry="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/></svg>SSL Secured Checkout</div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
